// @ts-nocheck
import {
  useEffect,
  useRef,
  useState,
  useCallback,
  Suspense,
  useImperativeHandle,
  Ref,
  forwardRef,
} from "react";
import { useNavigationStore } from "src/store/navigationStore";
import { useCrimeSceneStore } from "src/store/crimeSceneStore";

import { filterStraightLinePoints } from "src/utils/three";
import { useNavigate } from "react-router-dom";
import NavSlider from "@/components/controls/navigation/nav-slider";
import { SceneData } from "@/types/crime";
import {
  getTimeIndex,
  calculatePercentage,
  findObjectByName,
  findObjectByStringArrayName,
  createCharacterFrameObject,
  getAvatarColorByType,
  reduceDataByFraction,
} from "src/utils/simulation";
import {
  SOG_L1,
  SOG_L2,
  APPEAR_CHARACTER,
  ALL_SCENES_DATA,
} from "src/const/crimeSceneConst";
import { useHexagonSize } from "@/hooks/useHexagonSize";

import * as pc from "playcanvas";
import { OrbitControls } from "@playcanvas/react/scripts";
import {
  useTexture,
  useMaterial,
  useEnvAtlas,
  useApp,
  useSplat,
  useModel,
} from "@playcanvas/react/hooks";
import { Application, Entity, Gltf } from "@playcanvas/react";
import {
  Render,
  Camera,
  Light,
  GSplat,
  Script,
  Environment,
} from "@playcanvas/react/components";
import { CameraControls } from "playcanvas/scripts/esm/camera-controls.mjs";
import { useLodSplat } from "src/utils/useLodSplat";
import {
  vec3Distance,
  hexToRgbArray,
  getYAngleDegree,
  interpolateVector3,
} from "src/utils/game";

export default function CrimeSceneTopView() {
  const { HEX_WIDTH_INNER, HEX_HEIGHT_INNER } = useHexagonSize();
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth < 769);
    };

    checkIsMobile(); // Check on initial render
    window.addEventListener("resize", checkIsMobile);

    // Cleanup listener on component unmount
    return () => window.removeEventListener("resize", checkIsMobile);
  }, []);

  const latestProgressRef = useRef<number>(0);
  const isCameraUpdatingRef = useRef<boolean>(false);
  const latestSimulationDataRef = useRef<SceneData>(null);
  const latestCurrentLevelRef = useRef<string>("");
  const latestCurrentLevelIndexRef = useRef<string>(0);
  const latestFocusingCharacterRef = useRef<string>("");
  const updateCameraEventRef = useRef<number>(0);
  const latestSceneIndexRef = useRef<number>(0);

  const latestCameraPositionRef = useRef<pc.Vec3>();
  const latestCameraAngleRef = useRef<pc.Vec3>();

  let _localFocusingCharacter = "";

  const EachWalkPath = ({
    start,
    end,
    length,
    pathIndex,
    pathLength,
    name,
    color,
  }) => {
    const app = useApp();
    const entityRef = useRef();
    const meshInstanceRef = useRef(null);
    const currentMaterialRef = useRef(null);

    useEffect(() => {
      const meshInstance = entityRef.current.render.meshInstances[0];

      const newMaterial = new pc.StandardMaterial();
      currentMaterialRef.current = newMaterial;
      newMaterial.diffuse.set(...hexToRgbArray(color));
      newMaterial.emissive.set(...hexToRgbArray(color));
      newMaterial.emissiveIntensity = 1.0;
      newMaterial.useLighting = false;
      newMaterial.update();
      meshInstance.material = newMaterial;

      entityRef.current.setPosition(new pc.Vec3(start[0], start[1], -start[2]));
      entityRef.current.lookAt(new pc.Vec3(end[0], end[1], -end[2]));
      entityRef.current.setLocalScale(0.3, 0.3, length + 0.2);
      entityRef.current.translateLocal(0, 0, -length / 2);
      entityRef.current.enabled = false;
    }, [start, end, length, color]);

    useEffect(() => {
      app.on("update", frameUpdate);
      return () => app.off("update", frameUpdate);
    }, [app]);

    const frameUpdate = (dt: number) => {

      let isFocusingCharacterUpdated = _localFocusingCharacter != latestFocusingCharacterRef.current;
      _localFocusingCharacter = latestFocusingCharacterRef.current;

      if(isFocusingCharacterUpdated){
        if(latestFocusingCharacterRef.current == name){
          //change color
          //console.log(currentMaterialRef.current);
        }
        else{
          //change color
          //console.log(currentMaterialRef.current);
        }
      }

      if (entityRef.current == null) {
        return;
      }
      if(pathLength * latestProgressRef.current < pathIndex){
        entityRef.current.enabled = false;
      }
      else if(latestProgressRef.current == 0){
        entityRef.current.enabled = false;
      }
      else if(latestFocusingCharacterRef.current != "observationCamera" && latestFocusingCharacterRef.current != name){
        entityRef.current.enabled = false;
      }
      else{
        entityRef.current.enabled = true;
      }
    };

    return (
      <Entity ref={entityRef}>
        <Render type="box" />
      </Entity>
    );
  };

  const WalkPaths = ({
    name,
    points,
    color,
  }: {
    points: number[][];
    color: string;
  }) => {
    const segments = points.slice(0, -1).map((start, i) => {
      const end = points[i + 1];
      const length = Math.hypot(
        end[0] - start[0],
        end[1] - start[1],
        end[2] - start[2],
      );
      return (
        <EachWalkPath
          key={i}
          start={start}
          end={end}
          length={length}
          pathIndex={i + 1}
          pathLength={points.length - 1}
          name={name}
          color={color}
        />
      );
    });
    return (
      <>
        {segments}
        {/*<ExtraWalkPath points={points} />*/}
      </>
    );
  };

  const CharacterWalkPathsContainer = ({
    name,
    color,
    extraY,
  }: {
    name: string;
    color: string;
    extraY: number;
  }) => {
    const app = useApp();
    const isPointsSetupRef = useRef<boolean>(false);
    const [points, setPoints] = useState<number[][] | null>(null);
    let _localSceneIndex = 0;
    let _localFocusingCharacter = "";
    const [pathColor , setPathColor] = useState<string>("#FF0000");

    useEffect(() => {
      app.on("update", frameUpdate);
      return () => {
        app.off("update", frameUpdate);
      };
    }, [app]);

    const frameUpdate = (dt: number) => {
      let isSceneIndexUpdated = _localSceneIndex != latestSceneIndexRef.current;
      _localSceneIndex = latestSceneIndexRef.current;

      let isFocusingCharacterUpdated = _localFocusingCharacter != latestFocusingCharacterRef.current;
      _localFocusingCharacter = latestFocusingCharacterRef.current;

      if(isFocusingCharacterUpdated){
        if(_localFocusingCharacter == name){
          setPathColor("#FFFFFF");
        }
        else{
          setPathColor(color);
        }
      }

      if (isSceneIndexUpdated && latestProgressRef.current == 0) {
        isPointsSetupRef.current = false;
        setPoints(null);
      }
      if (
        isPointsSetupRef.current == false &&
        latestSimulationDataRef.current != null
      ) {
        let completePoints = getCompleteSimulationPath(
          latestSimulationDataRef.current,
          name,
          extraY ?? 0,
        );
        setPoints(completePoints);
        isPointsSetupRef.current = true;
      }



    };
    return points != null ? (
      <WalkPaths name={name} points={points} color={pathColor} />
    ) : (
      <></>
    );
  };

  const AllWalkPathsContainer = () => {
    return (
      <Entity>
        {APPEAR_CHARACTER.map((character: any, index) => {
          return (
            <CharacterWalkPathsContainer
              key={index}
              name={character.name}
              color={getAvatarColorByType(character.type)}
              extraY={character.topViewExtraY ?? 0}
            />
          );
        })}
      </Entity>
    );
  };

  function getCompleteSimulationPath(
    originalData: SceneData,
    character: string,
    extraY: number,
  ) {
    const animationData = reduceDataByFraction(originalData.animation, 1 / 4);
    const data = originalData;
    const dataIndex = animationData.length;
    let newPoints: [number, number, number][] = [];
    for (let index = 0; index < dataIndex; index++) {
      const charactersDataString = findObjectByStringArrayName(
        animationData[index].data,
        character,
      );
      if (charactersDataString != null) {
        const frameObject = createCharacterFrameObject(charactersDataString);
        const lastPoint = newPoints[newPoints.length - 1];
        newPoints.push([
          frameObject.position.x,
          frameObject.position.y + extraY,
          frameObject.position.z,
        ]);
      } else {
        // newPoints.push([0, 0, 0]);
      }
    }
    return newPoints;
  }

  const DataController = () => {
    // const progress = useCrimeSceneStore((state) => state.progress);
    const simulationData = useCrimeSceneStore((state) => state.simulationData);
    useEffect(() => {
      if (simulationData != null) {
        latestSimulationDataRef.current = simulationData;
      }
    }, [simulationData]);
    const focusingCharacter = useCrimeSceneStore(
      (state) => state.focusingCharacter,
    );
    useEffect(() => {
      latestFocusingCharacterRef.current = focusingCharacter;
    }, [focusingCharacter]);

    const currentLevel = useCrimeSceneStore((state) => state.currentLevel);
    useEffect(() => {
      latestCurrentLevelRef.current = currentLevel;
    }, [currentLevel]);
    const currentLevelIndex = useCrimeSceneStore(
      (state) => state.currentLevelIndex,
    );
    useEffect(() => {
      latestCurrentLevelIndexRef.current = currentLevelIndex;
    }, [currentLevelIndex]);

    const sceneIndex = useCrimeSceneStore((state) => state.sceneIndex);
    useEffect(() => {
      latestSceneIndexRef.current = sceneIndex;
    }, [sceneIndex]);

    const cameraPosition = useCrimeSceneStore((state) => state.cameraPosition);
    useEffect(() => {
      latestCameraPositionRef.current = cameraPosition;
    }, [cameraPosition]);

    const cameraAngle = useCrimeSceneStore((state) => state.cameraAngle);
    useEffect(() => {
      latestCameraAngleRef.current = cameraAngle;
    }, [cameraAngle]);

    return <></>;
  };

  const ProgressController = () => {
    const progress = useCrimeSceneStore((state) => state.progress);
    useEffect(() => {
      latestProgressRef.current = progress;
    }, [progress]);
    isCameraUpdatingRef.current = true;
    useEffect(() => {
      updateCameraEventRef.current = Date.now();
    }, []);
    return <></>;
  };

  const VenueSogs = () => {
    return (
      <Entity>
        <Entity>
          <SogSplat url={SOG_L1} floorIndex={1}></SogSplat>
          <SogSplat url={SOG_L2} floorIndex={2}></SogSplat>
        </Entity>
      </Entity>
    );
  };

  const SogSplat = ({
    url,
    floorIndex,
  }: {
    url: string;
    floorIndex: number;
  }) => {
    const app = useApp();
    const floorRef = useRef();

    useEffect(() => {
      app.on("update", frameUpdate);
      return () => app.off("update", frameUpdate);
    }, [app]);

    const frameUpdate = (dt: number) => {
      if (
        latestCurrentLevelIndexRef.current == null ||
        floorRef.current == null
      ) {
        return;
      }
      floorRef.current.enabled =
        latestCurrentLevelIndexRef.current == floorIndex;
    };

    const { asset } = useSplat(url);

    return asset ? (
      <Entity
        enabled={true}
        ref={floorRef}
        rotation={[180, 0, 0]}
        position={[0, 0, 0]}
        scale={[1, 1, 1]}
      >
        <GSplat asset={asset} />
      </Entity>
    ) : (
      <></>
    );
  };

  const MainCamera = () => {
    const app = useApp();
    const cameraScriptRef = useRef();
    const _finalCameraPositionRef = useRef<pc.Vec3>(new pc.Vec3(0, 0, 0));
    const _finalCameraLookingPositionRef = useRef<pc.Vec3>(
      new pc.Vec3(0, 0, 0),
    );

    useEffect(() => {
      app.on("update", frameUpdate);
      return () => app.off("update", frameUpdate);
    }, [app]);

    const frameUpdate = (dt: number) => {
      if (latestFocusingCharacterRef.current == "observationCamera") {
        if (
          ALL_SCENES_DATA[latestSceneIndexRef.current].topViewPosition != null
        ) {
          const topViewPosition =
            ALL_SCENES_DATA[latestSceneIndexRef.current].topViewPosition;
          _finalCameraLookingPositionRef.current.set(
            topViewPosition[0],
            topViewPosition[1],
            topViewPosition[2],
          );
          _finalCameraPositionRef.current.set(
            topViewPosition[0],
            topViewPosition[1] + 12,
            topViewPosition[2] + 0.001,
          );
        }
      } else {
        if (latestCameraPositionRef.current != null) {
          _finalCameraLookingPositionRef.current.set(
            latestCameraPositionRef.current.x,
            latestCameraPositionRef.current.y,
            latestCameraPositionRef.current.z,
          );
          _finalCameraPositionRef.current.set(
            latestCameraPositionRef.current.x,
            latestCameraPositionRef.current.y + 12,
            latestCameraPositionRef.current.z + 0.001,
          );
        }
      }
      cameraScriptRef.current.reset(
        _finalCameraLookingPositionRef.current,
        _finalCameraPositionRef.current,
      );
    };

    useEffect(() => {
      const defaultCameraLookingPosition = new pc.Vec3(0, 0, 0);
      const defaultPosition = new pc.Vec3(0, 20, 0.001);
      cameraScriptRef.current.reset(
        defaultCameraLookingPosition,
        defaultPosition,
      );
      cameraScriptRef.current.entity.camera.clearColor = new pc.Color(
        0.1,
        0.1,
        0.1,
        1,
      );
    }, []);

    return (
      <Entity>
        <Camera />
        <Script
          ref={cameraScriptRef}
          script={CameraControls}
          enableFly={false}
        />
      </Entity>
    );
  };

  const PlaycanvasScene = () => {
    const app = useApp();
    return (
      <>
        <AllWalkPathsContainer />
        <VenueSogs />
        <MainCamera />
      </>
    );
  };

  const TopDownScene = () => {
    return (
      <Application>
        <PlaycanvasScene />
      </Application>
    );
  };

  const DirectionLight = () => {
    const focusingCharacter = useCrimeSceneStore(
      (state) => state.focusingCharacter,
    );

    const cameraAngle = useCrimeSceneStore((state) => state.cameraAngle);
    return (
      <div
        className={`fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 ${focusingCharacter == "observationCamera" ? "hidden" : ""}`}
        style={{ transform: `rotate(${cameraAngle + 180}deg)` }}
      >
        {/*<div className="fixed w-[60px] h-[50px] left-1/2 -translate-x-1/2 -translate-y-full bg-gradient-to-t from-[#FFFFFFDD] to-[#FFFFFF00] [clip-path:polygon(0_0,100%_0,60%_100%,40%_100%)]"></div>*/}
        <div className="fixed w-[80px] left-1/2 -translate-x-1/2 -translate-y-full">
          <img
            src="/assets/spotlight.png"
            className="w-full h-full object-cover"
          />
        </div>
      </div>
    );
  };

  const LogoDot = () => {
    const focusingCharacter = useCrimeSceneStore(
      (state) => state.focusingCharacter,
    );
    const cameraAngle = useCrimeSceneStore((state) => state.cameraAngle);
    return (
      <div
        className={`${focusingCharacter == "observationCamera" ? "hidden" : ""}`}
        style={{ transform: `rotate(${cameraAngle + 180}deg)` }}
      >
        <img
          src="/assets/icon_logo_dot.png"
          className="w-full h-full object-cover"
        />
      </div>
    );
  };

  return (
    <div
      id="navi-top-view-parent"
      className={`relative hex-crop ${isMobile ? "mobile" : "desktop"}`}
      style={{
        width: `${HEX_WIDTH_INNER}px`,
        height: `${HEX_HEIGHT_INNER}px`,
      }}
    >
      <div
        className={`relative left-0 bottom-0 h-[100%] w-[100%] transform rotate-180 overflow-hidden`}
      >
        <div
          className={`absolute h-[100%] w-[100%] overflow-hidden bg-black`}
        />

        <div
          id="div-above-canvas"
          className="absolute left-0 bottom-0"
          style={{
            width: `${HEX_WIDTH_INNER}px`,
            height: `${HEX_HEIGHT_INNER}px`,
          }}
        >
          <DataController />
          <ProgressController />
          <TopDownScene />
        </div>

        <div className="absolute top-1/2 left-1/2 w-[20px] h-[20px] -translate-x-1/2 -translate-y-1/2">
          <DirectionLight />
          <LogoDot />
        </div>
      </div>
    </div>
  );
}
